import {useApi} from "@/composables/useApi";
import type { ProductData } from "@/types/products";
import type { AttributeData } from "@/types/attributes";

export const useAttributes = async () => {

    const attributes = useState<AttributeData[] | null>('attributes', () => null);
    const fetchingAttributes = useState('fetchingAttributes', () => false);

    const fetchAttributes = async () => {
        fetchingAttributes.value = true;
        const { fetch, data: attributesResponse } = useApi<AttributeData[]>({
            url: `attributes`,
            method: 'GET',
            query: {
                includes: ['options'],
                paginate: false,
            }
        });
        await fetch();
        attributes.value = attributesResponse.value;
        fetchingAttributes.value = false;
    };

    function getAttribute(code: string): AttributeData|undefined {
        return attributes.value?.find((attribute) => attribute.code === code);
    }

    function getAttributeType(code: string): string {
        return attributes.value?.find((attribute) => attribute.code === code)?.type ?? 'string';
    }

    function getAttributeLabel(code: string): string {
        return attributes.value?.find((attribute) => attribute.code === code)?.name ?? 'string';
    }

    function getAttributeOptionValue(code: string, value: number): string|undefined {
        return attributes.value?.find((attribute) => attribute.code === code)?.options?.find((o) => o.id === value)?.value;
    }

    function getAttributesByFilter(filter: Partial<AttributeData>): AttributeData[] {
        return attributes.value?.filter((attr: AttributeData) => {
            for (let attrKey of Object.keys(filter) as (keyof AttributeData)[]) {
                if (attr[attrKey] !== filter[attrKey]) {
                    return false;
                }
            }
            return true;
        }) ?? [];
    }

    function getProductAttributesByFilter(filter: Partial<AttributeData>, product: ProductData|ProductData & {is_favourited: boolean}): Record<string, any> {
        let filteredAttributes = getAttributesByFilter(filter);

        let keys = Object.keys(product.static_attributes) as Array<keyof ProductData['static_attributes']>;

        return keys.sort().reduce((acc: Record<string, string|number|boolean>, key) => {
            let relatedAttribute = filteredAttributes.find((a) => a.code === key);
            if(!relatedAttribute || product.static_attributes[key] === null) {
                return acc;
            }
            if(typeof product.static_attributes[key] === 'undefined' || (typeof product.static_attributes[key] === null && !relatedAttribute.is_gui_visible_when_null)) {
                return acc;
            }
            acc[key] = product.static_attributes[key];
            return acc;
        }, {});
    }

    if(!attributes.value && !fetchingAttributes.value) {
        await fetchAttributes();
    }

    return {
        attributes,
        fetchAttributes,
        getAttribute,
        getAttributeType,
        getAttributeLabel,
        getAttributeOptionValue,
        getAttributesByFilter,
        getProductAttributesByFilter
    }
}